import { Component } from "@angular/core"
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms"
import { MatButtonModule } from "@angular/material/button"
import { MatCardModule } from "@angular/material/card"
import { MatCheckboxModule } from "@angular/material/checkbox"
import { MatFormFieldModule } from "@angular/material/form-field"
import { MatIconModule } from "@angular/material/icon"
import { MatInputModule } from "@angular/material/input"
import { Router, RouterLink, RouterModule } from "@angular/router"
import { CognitoService } from "../../services/cognito.service"
import { BrandingService } from "../../services/branding.service"
import { AuthService } from "../../services/auth.service"
import { ProfileService } from "../../services/profile.service" // Add ProfileService import
import { SnackbarService } from "../../services/snackbar.service"
import { AuthFloatLabelDirective } from "../../directives/auth-float-label.directive"

@Component({
  selector: "app-login",
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    RouterModule,
    RouterLink,
    ReactiveFormsModule,
    AuthFloatLabelDirective,
  ],
  templateUrl: "./login.component.html",
  styleUrl: "./login.component.css",
})
export class LoginComponent {
  loginForm: FormGroup
  loading = false
  error = ""
  showPassword = false

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private cognitoService: CognitoService,
    private snackbar: SnackbarService,
    private brandingService: BrandingService,
    private authService: AuthService,
    private profileService: ProfileService, // Add ProfileService injection
  ) {
    this.loginForm = this.formBuilder.group({
      email: ["", [Validators.email, Validators.required]],
      password: ["", [Validators.minLength(6), Validators.required]],
    })
  }

  onSubmit(): void {
    if (this.loginForm.invalid) {
      this.markFormGroupTouched()
      return
    }

    this.loading = true
    this.error = ""

    const email = this.loginForm.get("email")?.value
    const password = this.loginForm.get("password")?.value

    this.cognitoService.signIn(email, password).subscribe({
      next: (response: any) => {
        console.log("Login response:", response)

        if (response.challengeName === "NEW_PASSWORD_REQUIRED") {
          // User needs to set a new password
          this.snackbar.info("Please set a new password")
          this.router.navigate(["/set-password"], {
            queryParams: {
              email: email,
              session: response.session,
            },
          })
        } else if (response.success && response.tokens) {
          this.profileService.ensureProfileLoaded(email).subscribe({
            next: () => {
              this.loadOwnerBranding()
              this.snackbar.success("Login successful!")
              this.router.navigate(["/userhome"])
              this.loading = false
            },
            error: () => {
              this.snackbar.success("Login successful!")
              this.router.navigate(["/userhome"])
              this.loading = false
            },
          })
        } else {
          this.error = response.message || "Login failed"
          this.snackbar.error(this.error)
          this.loading = false
        }
      },
      error: (error: any) => {
        console.error("Login error:", error)
        this.error = error.error?.message || "Login failed"
        this.snackbar.error(this.error)
        this.loading = false
      },
    })
  }

  private loadOwnerBranding(): void {
    // Get user info to check for ownerid
    const userInfo = this.authService.getCurrentUserInfo()

    // For now, we'll check if there's a way to get ownerid from the user data
    // This would need to be implemented based on your backend API
    // For demonstration, we'll assume the user data includes ownerid

    if (userInfo && userInfo.ownerid) {
      this.brandingService.loadUserBranding(userInfo.ownerid).subscribe({
        next: (owner) => {
          if (owner) {
            // Store owner branding data for app initialization
            localStorage.setItem("ownerBranding", JSON.stringify(owner))
            console.log("Owner branding applied:", owner)
          }
        },
        error: (error) => {
          console.error("Error loading owner branding:", error)
        },
      })
    }
  }

  private markFormGroupTouched(): void {
    Object.keys(this.loginForm.controls).forEach((key) => {
      const control = this.loginForm.get(key)
      control?.markAsTouched()
    })
  }

  get emailControl() {
    return this.loginForm.get("email")
  }

  get passwordControl() {
    return this.loginForm.get("password")
  }
}
